import React, { type ReactNode, useEffect, useRef, useState } from 'react'
import { motion } from 'framer-motion'
import { LucideArrowLeft } from 'lucide-react'
import classNames from 'classnames'
import * as styles from './FormStepper.module.scss'

interface FormStepper {
  steps: {
    id: string
    name: string
    component: React.ReactNode
    hideFromProgress?: boolean
  }[]
  currentStep: number
  setCurrentStep: (step: number) => void
  isPending?: boolean
  header?: ReactNode
  footer?: ReactNode
  showProgress?: boolean
  hideBackButton?: boolean
  sectionClassName?: string
}

const stepVariants = {
  hidden: (direction: number) => ({
    opacity: 0,
    x: direction > 0 ? 50 : -50, // Slide direction
    transition: {
      duration: 0.2,
      ease: 'easeInOut',
    },
  }),
  visible: {
    opacity: 1,
    x: 0,
    transition: {
      duration: 0.4,
      ease: 'easeInOut',
    },
  },
}

const defaultSectionClassName = styles.formStepperSection

export function FormStepper({
  steps,
  currentStep,
  setCurrentStep,
  isPending,
  header,
  footer,
  showProgress = true,
  hideBackButton = false,
  sectionClassName,
}: FormStepper) {
  const [isFirstRender, setIsFirstRender] = useState(true)
  const previousStep = useRef<number>(currentStep) // Track previous step
  const direction = currentStep > previousStep.current ? 1 : -1 // Determine

  useEffect(() => {
    previousStep.current = currentStep // Update previous step

    if (isFirstRender) {
      setIsFirstRender(false) // Set first render flag to false after first render
    }
  }, [currentStep])

  return (
    <section
      className={classNames([defaultSectionClassName, sectionClassName])}
    >
      {currentStep > 0 && !hideBackButton && (
        <div className={styles.backButtonContainer}>
          <button
            type="button"
            onClick={() => setCurrentStep(currentStep - 1)}
            className={classNames([
              styles.backButton,
              {
                [styles.disabled]: isPending,
              },
            ])}
          >
            <LucideArrowLeft className={styles.backButtonIcon} /> Back
          </button>
        </div>
      )}
      {header}
      <div className={styles.stepsContainer}>
        {steps.map((step, index) => (
          <motion.div
            key={step.id}
            className={classNames([
              styles.stepWrapper,
              {
                [styles.pending]: isPending && index !== currentStep,
              },
            ])}
            style={{
              position: index === currentStep ? 'relative' : 'absolute',
              top: 0,
              left: 0,
              zIndex: index === currentStep ? 1 : 0,
              width: '100%',
            }}
            custom={direction}
            variants={stepVariants}
            initial={isFirstRender && index === currentStep ? false : 'hidden'}
            animate={index === currentStep ? 'visible' : 'hidden'}
          >
            {step.component}
          </motion.div>
        ))}
      </div>

      {showProgress && (
        <nav className={styles.progressNav} aria-label="Progress">
          <ol className={styles.progressList}>
            {steps.map((step, index) => {
              const isActiveStep = index === currentStep
              const isCompletedStep = index < currentStep

              if (step.hideFromProgress) return null

              return (
                <li key={step.id} className={styles.progressItem}>
                  {isCompletedStep ? (
                    <div
                      className={classNames([
                        styles.progressBar,
                        styles.completed,
                      ])}
                    ></div>
                  ) : isActiveStep ? (
                    <div
                      className={classNames([
                        styles.progressBar,
                        styles.active,
                      ])}
                      aria-current="step"
                    ></div>
                  ) : (
                    <div
                      className={classNames([
                        styles.progressBar,
                        styles.inactive,
                      ])}
                    ></div>
                  )}
                </li>
              )
            })}
          </ol>
        </nav>
      )}
      {footer}
    </section>
  )
}
